import {
  canonicalEditorImportProject,
  hasEditorImportAdmission,
  useUploadStore,
} from "../../store/uploadStore";
import { editorBridgePort } from "../editor/editorBridgePort";
import {
  listDueLoaderValidationRows,
  persistLoaderValidationEvent,
  pruneExpiredLoaderValidationRows,
  recoverInterruptedLoaderValidationRows,
  updateLoaderValidationRow,
  type LoaderValidationOutboxRow,
} from "./loaderValidationOutbox";
import { submitLoaderValidationRow } from "./loaderValidationApi";
import { ApiError } from "../api/base";
import { auth } from "../../firebase";
import { appToast } from "../../utils/appToast";
import { cleanupConversionInput } from "../api/conversionInput";
import { getConversionReport, type ConversionReportResponse } from "../api/conversionReport";

export type LoaderValidationLocalResult = LoaderValidationOutboxRow & {
  projection?: ConversionReportResponse | null;
};

const activeLeases = new Set<string>();
let flushPromise: Promise<void> | null = null;

function retryDelayMs(attemptCount: number): number {
  const base = Math.min(2_000 * 2 ** Math.max(0, attemptCount - 1), 5 * 60_000);
  return Math.round(base * (0.8 + Math.random() * 0.4));
}

export type LoaderValidationSubmitDisposition = "retryable" | "terminal";

export function classifyLoaderValidationSubmitError(
  error: unknown,
): LoaderValidationSubmitDisposition {
  if (error instanceof ApiError) {
    return error.status === 500 || error.status === 503
      ? "retryable"
      : "terminal";
  }
  return error instanceof TypeError ? "retryable" : "terminal";
}

function loaderValidationSubmitErrorCode(error: unknown): string {
  if (error instanceof ApiError) return `http_${error.status}`;
  if (error instanceof TypeError) return "network_error";
  return error instanceof Error
    ? error.message.slice(0, 256)
    : "loader_validation_submit_failed";
}

async function submitRow(row: LoaderValidationOutboxRow): Promise<void> {
  if (activeLeases.has(row.attemptKey)) return;
  if (!auth.currentUser || auth.currentUser.uid !== row.ownerUid) return;
  activeLeases.add(row.attemptKey);
  try {
    const submitting = await updateLoaderValidationRow(
      row.attemptKey,
      (current) =>
        current.state === "pending"
          ? {
              ...current,
              state: "submitting",
              attemptCount: current.attemptCount + 1,
              updatedAt: Date.now(),
              lastErrorCode: undefined,
            }
          : current,
    );
    if (!submitting || submitting.state !== "submitting") return;

    const response = await submitLoaderValidationRow(submitting);
    if (response.loader_job_id !== submitting.loaderJobId) {
      throw new Error("loader_validation_response_id_mismatch");
    }
    await updateLoaderValidationRow(submitting.attemptKey, (current) => ({
      ...current,
      state: "accepted",
      nextAttemptAt: Number.MAX_SAFE_INTEGER,
      updatedAt: Date.now(),
      lastErrorCode: undefined,
    }));
  } catch (error) {
    const disposition = classifyLoaderValidationSubmitError(error);
    const retryable = disposition === "retryable";
    await updateLoaderValidationRow(row.attemptKey, (current) => ({
      ...current,
      state: retryable ? "pending" : "rejected",
      nextAttemptAt: retryable
        ? Date.now() + retryDelayMs(current.attemptCount)
        : Number.MAX_SAFE_INTEGER,
      updatedAt: Date.now(),
      lastErrorCode: loaderValidationSubmitErrorCode(error),
    })).catch(() => undefined);
  } finally {
    activeLeases.delete(row.attemptKey);
  }
}

export function flushLoaderValidationOutbox(): Promise<void> {
  if (flushPromise) return flushPromise;
  flushPromise = (async () => {
    const rows = await listDueLoaderValidationRows();
    await Promise.all(rows.map((row) => submitRow(row)));
  })().finally(() => {
    flushPromise = null;
  });
  return flushPromise;
}

export async function ingestLoaderValidationUnityEvent(payload: unknown): Promise<void> {
  const ownerUid = auth.currentUser?.uid ?? "";
  // Capture the item snapshot at receipt, but ownership is proven only by the
  // immutable runtime binding returned for this exact Loader attempt.
  const itemsAtReceipt = useUploadStore.getState().items;
  const row = await persistLoaderValidationEvent(payload, ownerUid);
  const scopedItem = Object.values(itemsAtReceipt).find(item => item.jobId === row.sourceConversionJobId && item.editorImportAdmission);
  const executionId = scopedItem?.editorImportAdmission?.executionId;
  const editorProject = canonicalEditorImportProject(
    scopedItem?.originViewrGcsPath,
  );
  const assertCurrent = () => {
    if (!scopedItem || !executionId) return;
    const current = useUploadStore.getState().items[scopedItem.id];
    if (!current || current.editorImportAdmission?.executionId !== executionId) throw new Error("loader_result_execution_stale");
  };
  let publishLocalResult = true;
  if (row.outcome === "failed") {
    assertCurrent();
    const cleanup = await editorBridgePort.cleanupLoaderAttempt(
      row.loaderJobId, row.generation,
      { skipStaleTransactionCheck: true, assertCurrent },
    );
    assertCurrent();
    if (scopedItem && executionId) {
      const current = useUploadStore.getState().items[scopedItem.id];
      const bindingMatches = cleanup?.ownerId === scopedItem.id && Boolean(cleanup?.executionId) &&
        current.editorImportAdmission?.runtimeExecutionId === cleanup.executionId;
      publishLocalResult = bindingMatches && hasEditorImportAdmission(current) && useUploadStore.getState().releaseEditorImport({
        id: scopedItem.id, executionId, jobId: row.sourceConversionJobId,
        runtimeExecutionId: cleanup.executionId, reason: "runtime_cleanup",
      });
    }
  }
  // Exact old-attempt cleanup can ACK its durable row, while a newer admitted
  // attempt receives no release, failure UI or model mutation.
  await editorBridgePort.ackLoaderValidationOutboxStored(
    row.loaderJobId, row.generation,
    { skipStaleTransactionCheck: true },
  );
  if (!publishLocalResult) {
    void flushLoaderValidationOutbox();
    return;
  }

  const openReport = () => {
    window.dispatchEvent(
      new CustomEvent("loaderValidation:openReport", {
        detail: { attemptKey: row.attemptKey },
      }),
    );
  };
  const cleanupOriginalInput = async (): Promise<boolean> => {
    if (!row.sourceConversionJobId) return false;
    try {
      await cleanupConversionInput(row.sourceConversionJobId);
      return true;
    } catch (error) {
      console.warn("[LoaderValidation] conversion input cleanup deferred", {
        sourceConversionJobId: row.sourceConversionJobId,
        error,
      });
      return false;
    }
  };
  // Converter warnings must survive a successful Loader result. The local UI
  // combines known Converter checks with this authoritative runtime terminal;
  // the durable outbox and Main API projection remain unchanged.
  let projection: ConversionReportResponse | null = null;
  if (row.outcome === "success" && auth.currentUser?.uid === row.ownerUid) {
    try {
      projection = await getConversionReport(
        await auth.currentUser.getIdToken(), row.sourceConversionJobId,
      );
      projection = {
        ...projection,
        loader_outcome: row.outcome,
        quality_state: projection.converter_outcome === "warning" ||
          row.workerFragment.events.some((event) => !event.code.endsWith("_SUCCESS"))
          ? "needs_review"
          : projection.materials.state === "not_checked" || projection.textures.state === "not_checked"
            ? "not_checked" : "no_issues",
      };
    } catch (error) {
      console.warn("[ConversionReport] local checks are unavailable", { jobId: row.sourceConversionJobId, error });
    }
  }
  assertCurrent();
  window.dispatchEvent(
    new CustomEvent("loaderValidation:result", { detail: { ...row, projection } }),
  );
  const hasWarning = projection?.converter_outcome === "warning" ||
    row.workerFragment.events.some((event) => !event.code.endsWith("_SUCCESS"));
  if (row.outcome === "failed") {
    appToast(
      "Import failed.",
      "error",
      null,
      { label: "View details", onAction: openReport },
      () => {
        void cleanupOriginalInput();
      },
      editorProject,
      undefined,
      row.attemptKey,
    );
  } else if (hasWarning || !projection || projection.quality_state === "not_checked") {
    appToast(
      "Import complete.",
      hasWarning ? "complete" : "notice",
      null,
      { label: "View details", onAction: openReport },
      () => {
        void cleanupOriginalInput();
      },
      editorProject,
      undefined,
      row.attemptKey,
    );
  } else {
    const originalInputWasRemoved = await cleanupOriginalInput();
    if (originalInputWasRemoved) {
      appToast(
        "Import complete. The original file is not stored on our servers.",
        "complete",
        3000,
        undefined,
        undefined,
        editorProject,
        undefined,
        row.attemptKey,
      );
    } else {
      appToast(
        "Import complete.",
        "complete",
        3000,
        undefined,
        undefined,
        editorProject,
        undefined,
        row.attemptKey,
      );
    }
  }
  void flushLoaderValidationOutbox();
}

export function startLoaderValidationRuntime(): () => void {
  let disposed = false;
  const flush = () => {
    if (!disposed) void flushLoaderValidationOutbox();
  };
  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") flush();
  };

  void Promise.all([
    recoverInterruptedLoaderValidationRows(),
    pruneExpiredLoaderValidationRows(),
  ]).then(flush).catch(() => undefined);
  window.addEventListener("online", flush);
  document.addEventListener("visibilitychange", onVisibilityChange);
  const intervalId = window.setInterval(flush, 30_000);

  return () => {
    disposed = true;
    window.removeEventListener("online", flush);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.clearInterval(intervalId);
  };
}
